import React, { useMemo } from 'react';
import { IDeliveryType } from '../../../models/configuration/plugin/IConfiguration';
import {
    StyledEmptyStateDiv
} from './DeliveryTypesList.styles';
import { DtArrangebleList, TreeItem } from '../../ui/list/DtArrangebleList';
import { DtListItem } from '../../ui/list';
import { Box, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DtAlert from '../../ui/alert/DtAlert';
import { ADDITIONAL_SECONDARY_PLACEHOLDERS, DELIVERY_PREFIX } from '../../../utils/constants';
import { isDynamicUuidSecondaryId } from '../../../utils/deliveryTypeHelper';

const getChildTreeLabel = (caption: string | undefined, childId: string, parentId: string): string => {
    if (caption) {
        return caption;
    }
    if (isDynamicUuidSecondaryId(childId, parentId)) {
        return ADDITIONAL_SECONDARY_PLACEHOLDERS[parentId] ?? childId;
    }
    return childId;
};

// Only these top-level parent delivery types support adding an unlimited number of
// dynamic secondary methods; all other top-level nodes (Collaboration, eMail, Mail, etc.)
// never render the "+" control, regardless of whether an onAdd handler is supplied.
const ADD_ELIGIBLE_PARENT_IDS: string[] = [
    DELIVERY_PREFIX.ESIGN,
    DELIVERY_PREFIX.EDISCLOSE,
    DELIVERY_PREFIX.ECLOSE
];

const canAddSecondaryUnder = (parentId: string): boolean =>
    ADD_ELIGIBLE_PARENT_IDS.includes(parentId.toLowerCase());

interface DeliveryTypesListProps {
    deliveryTypes: IDeliveryType[] | null,
    selectedDeliveryTypeIdx: string | null,
    selectDeliveryType: (id: string) => void,
    deliveryTypeTree: any[] | null,
    onListOrderChange?: (event: { parent: string | null, arrangedItems: { item: string | number, Order: number }[] }) => void,
    onAdd?: (parentId: string) => void
}


// Helper to convert tree to DtListItem[]
const toDtListItems = (
    nodes: any[],
    selectedDeliveryTypeIdx: string | null,
    selectDeliveryType: (id: string) => void,
    onAdd?: (parentId: string) => void
): DtListItem[] => {
    return nodes.map(node => ({
        id: node.Id,
        // Fall back to the Id when Caption is blank (e.g. a just-created, not-yet-captioned
        // dynamic secondary method) so the row never renders as an unlabeled empty pill.
        label: node.Caption || node.Id,
        checked: selectedDeliveryTypeIdx === node.Id,
        disabled: !node.Enabled,
        onClick: () => selectDeliveryType(node.Id),
        Order: node.Order,
        actionConfig: (onAdd && canAddSecondaryUnder(node.Id))
            ? {
                actions: [
                    {
                        type: 'custom',
                        label: 'Add',
                        icon: <AddIcon fontSize="small" />,
                        onClick: () => onAdd(node.Id)
                    }
                ]
            }
            : undefined,
        children: node.children
            ? node.children.map((dtc: any) => ({
                id: dtc.Id,
                label: getChildTreeLabel(dtc.Caption, dtc.Id, node.Id),
                checked: selectedDeliveryTypeIdx === dtc.Id,
                disabled: !dtc.Enabled,
                onClick: () => selectDeliveryType(dtc.Id),
                Order: dtc.Order,
            }))
            : undefined
    }));
};

const DeliveryTypesList: React.FC<DeliveryTypesListProps> =
    ({selectedDeliveryTypeIdx, selectDeliveryType, deliveryTypeTree, onListOrderChange, onAdd}) => {
        // Memoize items for performance
        const items = useMemo(() =>
            deliveryTypeTree
                ? toDtListItems(deliveryTypeTree, selectedDeliveryTypeIdx, selectDeliveryType, onAdd)
                : []
        , [deliveryTypeTree, selectedDeliveryTypeIdx, selectDeliveryType, onAdd]);

        return (
            <Box>
                <Box sx={{ 
                    maxHeight: { md: '52vh', lg: '66vh', xl: '76vh' }, 
                    overflowY: 'auto'
                }}>
                    <DtAlert severity='info'>
                        Drag items to rearrange.
                    </DtAlert>
                    {deliveryTypeTree &&
                        (deliveryTypeTree.length === 0 ?
                            <StyledEmptyStateDiv>No delivery types found</StyledEmptyStateDiv>
                            :
                        <Box sx={{ pr: 0.5}}>
                            <DtArrangebleList
                                items={items}
                                selectable={false}
                                dense={false}
                                onArrange={event => {
                                    if (typeof onListOrderChange === 'function') {
                                        onListOrderChange(event);
                                    }
                                }}
                                onClick={(item: TreeItem) => {
                                    selectDeliveryType(item.id as string);
                                }}
                                collapsibleChildren={true}
                                defaultExpanded={false}
                                syncOnChange={false}
                            />
                        </Box>
                        )}
                </Box>
            </Box>
        );
    }

export default DeliveryTypesList;